import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { api, errMsg } from "@/lib/api";
import { useAuth, homeFor } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";

export const AuthShell = ({ title, sub, children }) => (
  <div className="container-x py-12 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
    <div className="hidden lg:block relative h-[520px]">
      <img src="/demo/hero.jpg" alt="" className="absolute inset-0 w-full h-full object-cover rounded-[2rem]" />
      <div className="absolute inset-x-6 bottom-6 rounded-2xl bg-white/90 backdrop-blur p-5 text-sm text-slate-700">Every companion is age & identity verified. Strictly platonic. Meet only in public.</div>
    </div>
    <div className="max-w-md w-full mx-auto space-y-6">
      <div><h1 className="text-4xl sm:text-5xl font-bold">{title}</h1><p className="text-slate-600 mt-2">{sub}</p></div>
      {children}
    </div>
  </div>
);

export default function Login() {
  const { t } = useLang();
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", f);
      signIn(data.token, data.user);
      const next = params.get("next");
      nav(next && next.startsWith("/") ? next : homeFor(data.user));
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t("login")} sub="Welcome back to RentMyDate.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Input data-testid="login-email-input" type="email" placeholder="Email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <PasswordInput data-testid="login-password-input" placeholder="Password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        {err && <p className="text-sm text-rose-600" data-testid="login-error">{err}</p>}
        <div className="text-right"><Link to="/forgot-password" className="text-sm font-semibold text-[#0D5C46]" data-testid="forgot-password-link">Forgot password?</Link></div>
        <Button data-testid="login-form-submit-button" className="w-full h-11 rounded-full" disabled={busy}>{busy ? <Loader2 className="animate-spin" /> : t("login")}</Button>
      </form>
      <p className="text-sm text-slate-600">New here? <Link to="/register" className="font-semibold text-[#0D5C46]" data-testid="login-to-register-link">Create an account</Link> · <Link to="/become-companion" className="font-semibold text-[#0D5C46]">Apply as companion</Link></p>
    </AuthShell>
  );
}
